import { z } from 'zod';

import type { ToolDefinition } from '../types';

type Params = ToolDefinition['function']['parameters'];

/**
 * Convert a Zod schema into the plain JSON Schema the LLM sees for a tool's
 * parameters. The emitted object is sent verbatim to the model (OpenAI Responses
 * `parameters` and Anthropic `input_schema`), so it must stay free of the
 * JSON-Schema authoring keywords (`$schema`, `$defs`/`definitions`, `$ref`) that
 * providers reject or that would change what the model sees. `reused: 'inline'`
 * keeps repeated sub-schemas inline rather than hoisting them into `$defs`.
 */
export function toLLMJSONSchema(schema: z.ZodType): Params {
    const json = z.toJSONSchema(schema, {
        target: 'draft-7',
        // Describe the schema as the model should PRODUCE it: optional fields stay
        // optional rather than being treated as required output.
        io: 'input',
        // Never hoist repeated sub-schemas into $defs/$ref — the model must see a
        // fully-inlined schema.
        reused: 'inline',
    });
    delete (json as { $schema?: unknown }).$schema;
    delete (json as { $defs?: unknown }).$defs;
    delete (json as { definitions?: unknown }).definitions;
    // Zod's integer type (`z.int()` / `z.number().int()`) bakes in the JS safe-integer
    // range as `minimum`/`maximum`. Hand-written tool schemas write a bare `type: 'integer'`,
    // so strip those sentinel bounds to stay wire-identical. Any real bound the author set
    // (e.g. `.min(0)`) is a different value and is kept.
    stripSafeIntegerBounds(json);
    // A plain `z.object` with no required fields makes `z.toJSONSchema` omit `required`
    // entirely, but hand-written tool schemas always include it (even empty). Restore the
    // convention so an all-optional schema-first tool stays wire-identical to its legacy form.
    if ((json as { required?: unknown }).required === undefined) {
        (json as { required: string[] }).required = [];
    }
    // `reused: 'inline'` inlines *repeated* sub-schemas, but a sub-schema carrying a
    // registry id (e.g. via `.meta({ id })`) is hoisted into `definitions` with a
    // `$ref` regardless. Having just dropped `definitions`, any surviving `$ref`
    // would dangle — a schema pointing at nothing, shipped to the model silently.
    // Fail loudly instead so the author inlines the sub-schema or drops its id.
    if (JSON.stringify(json).includes('"$ref":')) {
        throw new Error(
            'Tool schema emitted a $ref (a sub-schema with a registry id was hoisted); ' +
                'inline the sub-schema or remove its id so the model gets a self-contained schema.',
        );
    }
    return json as Params;
}

/**
 * Recursively delete the JS safe-integer sentinel `minimum`/`maximum` that Zod emits for
 * integer schemas, so `type: 'integer'` ships bare (matching the hand-written schemas). A
 * real author-set bound has a different value and is left untouched.
 */
function stripSafeIntegerBounds(node: unknown): void {
    if (Array.isArray(node)) {
        node.forEach(stripSafeIntegerBounds);
        return;
    }
    if (!node || typeof node !== 'object') {
        return;
    }
    const obj = node as Record<string, unknown>;
    if (obj.type === 'integer') {
        if (obj.minimum === Number.MIN_SAFE_INTEGER) {
            delete obj.minimum;
        }
        if (obj.maximum === Number.MAX_SAFE_INTEGER) {
            delete obj.maximum;
        }
    }
    for (const value of Object.values(obj)) {
        stripSafeIntegerBounds(value);
    }
}

/**
 * Format Zod validation issues into a single human-readable string for the
 * corrective error message handed back to the model when args fail validation.
 */
export function formatZodIssues(error: z.ZodError): string {
    return error.issues.map((i) => `${i.path.join('.') || 'root'}: ${i.message}`).join(', ');
}
